import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";
import img1 from "../schoolgirl.jpg";

const CSS_MaxWidth = () => {
  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">📏 CSS Max-Width</h1>

      <p className="tutorial-intro">
        The <code>max-width</code> property in CSS is used to set the maximum
        width of an element. It prevents the element from growing beyond the
        defined width, while still allowing it to shrink if needed. This is
        especially useful for making layouts responsive.
      </p>

      {/* Highlight Box */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem",
          borderRadius: "12px",
          marginBottom: "2rem",
          textAlign: "center",
        }}
      >
        <h2 style={{ color: "#92400e", marginBottom: "0.5rem" }}>
          ✨ Why Use max-width?
        </h2>
        <p style={{ fontSize: "1.1rem", lineHeight: "1.6" }}>
          It helps keep content readable by preventing it from stretching too
          wide on large screens. Commonly used in responsive design for text
          blocks, images, and containers.
        </p>
      </div>

      {/* Example 1 */}
      <div className="tutorial-section">
        <h2 className="section-title">🔹 Basic Example</h2>
        <p>
          In this example, the paragraph has a <code>max-width</code> of{" "}
          <code>600px</code>. On larger screens, it won’t stretch beyond this
          limit.
        </p>

        <CodeCopyBlock
          code={`p {
  max-width: 600px;
  background: #fef9c3;
  padding: 10px;
}`}
        />

        <div style={{ marginTop: "1rem" }}>
          <p
            style={{
              maxWidth: "600px",
              background: "#fef9c3",
              padding: "10px",
              border: "1px solid #facc15",
              borderRadius: "6px",
            }}
          >
            This paragraph will never grow wider than 600px, even if the screen
            size is much larger.
          </p>
        </div>
      </div>

      {/* Example 2 */}
      <div className="tutorial-section">
        <h2 className="section-title">🔹 max-width vs width</h2>
        <p>
          If you use <code>width</code>, the element is fixed. But{" "}
          <code>max-width</code> allows flexibility. Resize the screen to see
          the difference:
        </p>

        <CodeCopyBlock
          code={`.fixed-box {
  width: 600px;
}

.flexible-box {
  max-width: 600px;
}`}
        />

        <div
          style={{
            marginTop: "1rem",
            display: "flex",
            gap: "10px",
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              width: "600px",
              background: "#fca5a5",
              padding: "10px",
              borderRadius: "6px",
              flex: "1 1 auto",
            }}
          >
            Fixed Width (600px)
          </div>
          <div
            style={{
              maxWidth: "600px",
              background: "#86efac",
              padding: "10px",
              borderRadius: "6px",
              flex: "1 1 auto",
            }}
          >
            Flexible Max-Width (600px)
          </div>
        </div>
      </div>

      {/* Example 3 */}
      <div className="tutorial-section">
        <h2 className="section-title">🔹 max-width for Images</h2>
        <p>
          By using <code>max-width: 100%</code>, images scale down to fit the
          parent container while keeping their aspect ratio.
        </p>

        <CodeCopyBlock
          code={`img {
  max-width: 100%;
  height: auto;
}`}
        />

        <div
          style={{
            marginTop: "1rem",
            background: "#e0f2fe",
            padding: "10px",
            borderRadius: "6px",
          }}
        >
          <img
            src={img1}
            alt="Sample"
            style={{
              maxWidth: "100%",
              height: "auto",
              border: "2px solid #0284c7",
              borderRadius: "6px",
            }}
          />
        </div>
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">🚀 Next Steps</h2>
        <p>
          Now that you understand <code>max-width</code>, try exploring{" "}
          <a href="/css/minwidth" className="learn-more">
            CSS Min-Width
          </a>{" "}
          and{" "}
          <a href="/css/width" className="learn-more">
            CSS Width
          </a>{" "}
          for more control over element sizing.
        </p>
      </div>
    </div>
  );
};

export default CSS_MaxWidth;
